/**
 * Protected Route Component
 * Handles route protection based on authentication and approval status
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Permission, rbacManager } from '@/services/roleBasedAccess';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface ProtectedRouteProps {
  children: ReactNode;
  requireApproval?: boolean;
  fallbackPath?: string;
  requiredPermissions?: Permission[];
  requireAllPermissions?: boolean;
}

export const ProtectedRoute = ({
  children,
  requireApproval = true,
  fallbackPath = '/login',
  requiredPermissions = [],
  requireAllPermissions = true,
}: ProtectedRouteProps) => {
  const { isAuthenticated, isApproved, isPending, loading, user } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center justify-center text-center">
          <LoadingSpinner size="lg" />
          <p className="text-gray-600 mt-4">Verificando autenticación...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // If approval is required but user is pending
  if (requireApproval && isPending) {
    return <Navigate to="/pending-approval" replace />;
  }

  // If approval is required but user is not approved
  if (requireApproval && !isApproved) {
    return <Navigate to="/login" replace />;
  }

  // Check permissions if required
  if (requiredPermissions.length > 0) {
    const { hasAccess, missingPermissions } = rbacManager.validateAccess(
      user,
      requiredPermissions,
      requireAllPermissions
    );

    if (!hasAccess) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Acceso Denegado
            </h2>
            <p className="text-gray-600 mb-4">
              No tienes los permisos necesarios para acceder a esta página.
            </p>
            {missingPermissions.length > 0 && (
              <p className="text-sm text-gray-500 mb-6">
                Permisos requeridos: {missingPermissions.join(', ')}
              </p>
            )}
            <button
              onClick={() => window.history.back()}
              className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
            >
              Volver
            </button>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};

/**
 * Public Route Component
 * Redirects authenticated users away from public pages (like login)
 */
interface PublicRouteProps {
  children: ReactNode;
  redirectTo?: string;
}

export const PublicRoute = ({
  children,
  redirectTo = '/dashboard',
}: PublicRouteProps) => {
  const { isAuthenticated, isApproved, isPending, loading } = useAuth();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center justify-center text-center">
          <LoadingSpinner size="lg" />
          <p className="text-gray-600 mt-4">Cargando...</p>
        </div>
      </div>
    );
  }

  // Redirect approved users to dashboard
  if (isAuthenticated && isApproved) {
    return <Navigate to={redirectTo} replace />;
  }

  // Redirect pending users to pending approval page
  if (isAuthenticated && isPending) {
    return <Navigate to="/pending-approval" replace />;
  }

  return <>{children}</>;
};
